"use client"

import { useState, useCallback, useTransition, useEffect, useMemo } from "react"
import { Flex, Loader, useMediaQuery } from "@forestsync/ui"
import { FilterSearchPanel } from "./FilterSearchPanel"
import { ForestDetailsCard } from "./ForestDetailsCard"
import type { CarbonSinkWithGeo, MapFilters } from "@/types/glassroom"
import { getCountryCode, getCountryName } from "@forestsync/db"
import "./forestmap.css"
import dynamic from "next/dynamic"

// Simple fallback component
const MapFallback = () => (
  <div className="flex h-full w-full items-center justify-center p-4">
    <Loader type="bars" size="xl" />
  </div>
);

// Lazy load the heavy map component
const DynamicMap = dynamic(() => import("./DynamicMap"), {
  ssr: false,
  loading: () => <MapFallback />,
});

interface ForestMapProps {
  mapData: CarbonSinkWithGeo[]
}

const DEFAULT_FILTERS: MapFilters = {
  searchTerm: "",
  country: null,
  region: null,
  municipality: null,
  creditType: null,
  methodology: null,
  projectType: null,
}

export function ForestMap({ mapData }: ForestMapProps) {
  const [selectedItem, setSelectedItem] = useState<string | null>(null)
  const [mapCenter, setMapCenter] = useState<[number, number]>([48, 9])
  const [isDetailsVisible, setIsDetailsVisible] = useState(false)
  const [isPending, startTransition] = useTransition()
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [filters, setFilters] = useState<MapFilters>(DEFAULT_FILTERS)

  // Use Mantine's hook for responsive design instead of manual event listeners
  const isMobile = useMediaQuery(`(max-width: 62em)`, false)

  // Initialize sidebar state based on mobile status
  useEffect(() => {
    setSidebarOpen(!isMobile)
  }, [isMobile])

  const toggleSidebar = useCallback(() => {
    setSidebarOpen((prev) => !prev)
  }, [])

  const handleFiltersChange = useCallback((partial: Partial<MapFilters>) => {
    setFilters((prev) => {
      const next = { ...prev, ...partial }
      if ("country" in partial && partial.country !== prev.country) {
        next.region = null
        next.municipality = null
      }
      if ("region" in partial && partial.region !== prev.region) {
        next.municipality = null
      }
      if ("creditType" in partial && partial.creditType !== prev.creditType) {
        next.methodology = null
      }
      return next
    })
  }, [])

  const handleResetFilters = useCallback(() => setFilters(DEFAULT_FILTERS), [])

  const filteredData = useMemo(() => {
    return mapData.filter((forest) => {
      if (
        filters.searchTerm &&
        !forest.name.toLowerCase().includes(filters.searchTerm.toLowerCase()) &&
        !getCountryName(forest.country).toLowerCase().includes(filters.searchTerm.toLowerCase())
      )
        return false
      if (filters.country && getCountryCode(forest.country) !== filters.country) return false
      if (filters.region && forest.region !== filters.region) return false
      if (filters.municipality && forest.municipality !== filters.municipality) return false
      if (filters.creditType && forest.creditType !== filters.creditType) return false
      if (filters.methodology && forest.methodology !== filters.methodology) return false
      if (filters.projectType && String(forest.projectType) !== filters.projectType) return false
      return true
    })
  }, [mapData, filters])

  const selectedForest = mapData.find((m) => m.refId === selectedItem)

  // Handle item click with smooth transitions
  const handleItemClick = useCallback(
    (refId: string) => {
      startTransition(() => {
        setSelectedItem(refId)
        setIsDetailsVisible(true)
        const item = mapData.find((m) => m.refId === refId)
        if (item && item.geoLocation) {
          setMapCenter([item.geoLocation.lat, item.geoLocation.lng])
        }
      })
    },
    [mapData],
  )

  return (
    <Flex className="h-full w-full flex flex-col md:flex-row relative">
      {/* Left side: FilterSearchPanel with lifted filter state */}
      <FilterSearchPanel
        mapData={mapData}
        filteredData={filteredData}
        filters={filters}
        onFiltersChange={handleFiltersChange}
        onResetFilters={handleResetFilters}
        onSelectForest={handleItemClick}
        selectedItem={selectedItem}
        sidebarOpen={sidebarOpen}
        toggleSidebar={toggleSidebar}
      />

      {/* Right side: Map and details popup */}
      <div className="flex-1 relative z-10">
        <DynamicMap
          mapData={filteredData}
          selectedItem={selectedItem}
          mapCenter={mapCenter}
          onItemClick={handleItemClick}
          isSidebarOpen={sidebarOpen}
          toggleSidebar={toggleSidebar}
          isMobile={!!isMobile}
        />

        {/* Forest details card in bottom right */}
        {isDetailsVisible && selectedItem && selectedForest && !isPending && (
          <div className="absolute bottom-4 right-4 w-fit z-999 shadow-xl">
            <ForestDetailsCard
              sink={selectedForest}
              onClose={() => setIsDetailsVisible(false)}
            />
          </div>
        )}
      </div>
    </Flex>
  )
}
